import { useQuery } from '@tanstack/react-query';
import { FileData } from 'src/data-capture/hooks/file-uploads/types';

type MeetingNoteTense = 'THIRD_PERSON' | 'FIRST_SECOND_PERSON';

export type TemplateType =
  | 'SUITABILITY_REPORT'
  | 'MEETING_AGENDA'
  | 'MEETING_NOTE'
  | 'CLIENT_INFORMATION_DOCUMENT';

export type SectionType = 'CONTENT' | 'TABLE_OF_CONTENTS';

export type TemplateSection = {
  id: string;
  templateText: string;
  sectionHeader: string;
  sectionHeaderLevel: number;
  sectionInstructions: string;
  sectionPreview?: string;
  preserveAsIs: boolean;
  isTemplateLocked?: boolean;
  isInstructionsLocked?: boolean;
  isInstructionsComplete?: boolean;
  agent?: string;
  domainFilter?: string[];
  addPageBreakAfterSection?: boolean;
  sectionType?: SectionType;
  tableOfContentsDepth?: number;
  addCoverPageBeforeSection?: boolean;
};

export type UploadedTemplate = {
  id: string;
  file?: FileData;
  displayName: string;
  description: string;
  examplePopulatedFileId?: string;
  exampleTemplatedFileId?: string;
  supportsMulticlient: boolean;
  eligibleForSummarisation: boolean;
  templateType: TemplateType;
  isActive: boolean;
  isDefault: boolean;
  sections: TemplateSection[];
  createdAt?: string;
  markdownImageCollectionId: string | null;
  meetingNoteTense?: MeetingNoteTense;
  sectionSplittingLevel?: number | null;
};

export type TemplateWithoutSections = Omit<UploadedTemplate, 'sections'>;

export default function useTemplates({
  companyId,
  templateType,
}: {
  companyId?: string;
  templateType?: TemplateType;
}) {
  const isAdminEndpoint = !!companyId;

  const params = templateType ? { templateType } : undefined;

  const queryKey: unknown[] = isAdminEndpoint
    ? ['admin', 'companies', companyId, 'data-export-templates']
    : ['data-export-templates'];

  if (params) {
    queryKey.push(params);
  }

  const { data, isLoading, isError, refetch } = useQuery<TemplateWithoutSections[]>({
    queryKey,
  });

  return {
    files: data || [],
    isLoading,
    isError,
    refetch,
  };
}
